import { EnquiryForm, Interest } from "@components/shared/EnquiryForm";
import { Section, SectionHeading } from "@components/shared/Section";
import { cn } from "@/ui/utils/cn";
import { Check } from "lucide-react";
import { ReactNode } from "react";

/** Heading plus a check-marked list, in one or two columns. */
export const Checklist = ({
  title,
  items,
  columns = 2,
  tone = "white",
  lead,
}: {
  title: string;
  items: string[];
  columns?: 1 | 2;
  tone?: "white" | "mist";
  lead?: string;
}) => (
  <Section tone={tone}>
    <div className="grid gap-10 md:grid-cols-[0.8fr_1.2fr] md:gap-16">
      <SectionHeading title={title} lead={lead} />
      <ul className={cn("grid gap-4", columns === 2 && "sm:grid-cols-2")}>
        {items.map((item) => (
          <li
            key={item}
            className="flex items-start gap-3 text-body text-slate-700"
          >
            <span className="mt-1 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-mint-soft text-mint-ink">
              <Check size={14} aria-hidden="true" />
            </span>
            {item}
          </li>
        ))}
      </ul>
    </div>
  </Section>
);

/** Heading plus one paragraph (or custom children) in the two-column rhythm. */
export const TextBlock = ({
  title,
  body,
  children,
  tone = "white",
}: {
  title: string;
  body?: string;
  children?: ReactNode;
  tone?: "white" | "mist";
}) => (
  <Section tone={tone}>
    <div className="grid gap-8 md:grid-cols-[0.8fr_1.2fr] md:gap-16">
      <SectionHeading title={title} />
      <div className="max-w-prose text-body text-slate-700">
        {body ? <p>{body}</p> : null}
        {children}
      </div>
    </div>
  </Section>
);

/** Heading, one line and the callback form, on mist. */
export const FormBlock = ({
  title,
  body,
  interest,
  placement,
  id = "contact-form",
  tone = "mist",
}: {
  title: string;
  body: string;
  interest: Interest;
  placement: string;
  id?: string;
  tone?: "white" | "mist";
}) => (
  <Section tone={tone} id={id} className="scroll-mt-20">
    <div className="grid gap-10 md:grid-cols-[0.8fr_1.2fr] md:gap-16">
      <SectionHeading title={title} lead={body} />
      <div
        className={cn(
          "rounded-3xl p-6 md:p-8",
          tone === "mist" ? "bg-white" : "bg-mist",
        )}
      >
        <EnquiryForm
          defaultInterest={interest}
          lockInterest
          placement={placement}
        />
      </div>
    </div>
  </Section>
);
